import { useEffect, useState } from "react";
import { ridersAPI, ratingsAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { RefreshCw } from "lucide-react";
import { BranchBadge, BranchTabs, useBranchSplit } from "../components/ui/BranchTabs";
import toast from "react-hot-toast";

const statusDot = (s) => {
  const map = { available:"dot-green", busy:"dot-amber", offline:"dot-grey" };
  return <span className={`dot ${map[s]||"dot-grey"}`}/>;
};

export default function Riders() {
  const { user: me } = useAuth();
  const isBranchMgr  = me?.role === "branch_manager";

  const [riders, setRiders]           = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [tab, setTab]                 = useState("mine");
  const [mapRider, setMapRider]       = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([ridersAPI.available(), ratingsAPI.leaderboard()]);
      setRiders(r1.data.data || []);
      setLeaderboard(r2.data.data || []);
    } catch { toast.error("Failed to load riders"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const { mine, others, visible } = useBranchSplit(riders, isBranchMgr ? tab : "all");

  return (
    <div>
      <style>{`
        .riders-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .riders-mobile-cards  { display: none; }
        .riders-desktop-table { display: block; }
        .rider-card {
          padding: 14px 16px;
          border-bottom: 1px solid var(--border);
        }
        .rider-card:last-child { border-bottom: none; }
        @media (max-width: 900px) {
          .riders-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 768px) {
          .riders-mobile-cards  { display: block; }
          .riders-desktop-table { display: none; }
        }
      `}</style>

      <div className="page-header">
        <div>
          <div className="page-title">Riders</div>
          <div className="page-subtitle">{riders.length} currently online</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={14}/> Refresh</button>
      </div>

      <BranchTabs activeTab={tab} onChange={setTab} myCount={mine.length} otherCount={others.length}/>

      {isBranchMgr && tab === "others" && (
        <div style={{display:"flex",alignItems:"center",gap:10,background:"rgba(90,90,114,0.1)",border:"1px solid rgba(90,90,114,0.2)",borderRadius:10,padding:"10px 16px",marginBottom:16,fontSize:13}}>
          <span style={{fontSize:18}}>🏢</span>
          <span style={{color:"var(--text2)"}}>
            Riders from <strong style={{color:"var(--text)"}}>other branches</strong>. View only — useful for cross-branch coordination.
          </span>
        </div>
      )}

      <div className="riders-grid">
        {/* Online riders */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">🟢 Online Riders</span>
            <span style={{fontSize:12,color:"var(--text3)"}}>{visible.length} shown</span>
          </div>
          {loading
            ? <div className="loading-center"><div className="spinner"/></div>
            : (
            <>
              {/* Mobile cards */}
              <div className="riders-mobile-cards">
                {visible.length === 0
                  ? <div className="empty-state"><p>No online riders</p></div>
                  : visible.map(r => (
                  <div key={r.id} className="rider-card" style={{background:(isBranchMgr && !r.is_my_branch)?"rgba(90,90,114,0.04)":undefined}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                      <div style={{
                        width:36,height:36,borderRadius:8,flexShrink:0,
                        background:"linear-gradient(135deg,var(--accent),var(--accent2))",
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontWeight:700,fontSize:14,color:"#000"
                      }}>
                        {r.name?.[0]?.toUpperCase()}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:14}}>{r.name}</div>
                        <div style={{fontSize:11,color:"var(--text3)"}}>{r.phone}</div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{color:"var(--accent)",fontWeight:700,fontSize:13}}>★ {parseFloat(r.rating||0).toFixed(1)}</div>
                        <div style={{fontSize:10,color:"var(--green)"}}>● Online</div>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                      <span style={{fontSize:12,color:"var(--text2)",textTransform:"capitalize"}}>{r.vehicle_type}</span>
                      <BranchBadge branchName={r.branch_name||(r.branch_id?`Branch #${r.branch_id}`:null)} isMyBranch={!!r.is_my_branch} compact/>
                      {r.current_lat && (
                        <button className="btn btn-ghost btn-sm" style={{marginLeft:"auto"}} onClick={() => setMapRider(r)}>
                          📍 View Map
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="riders-desktop-table table-wrap">
                <table>
                  <thead>
                    <tr><th>Rider</th><th>Vehicle</th><th>Branch</th><th>Rating</th><th>Status</th><th>Location</th></tr>
                  </thead>
                  <tbody>
                    {visible.length === 0
                      ? <tr><td colSpan={6}><div className="empty-state"><p>No online riders</p></div></td></tr>
                      : visible.map(r => (
                      <tr key={r.id} style={{background:(isBranchMgr && !r.is_my_branch)?"rgba(90,90,114,0.04)":undefined}}>
                        <td>
                          <div className="fw-bold">{r.name}</div>
                          <div className="mono" style={{color:"var(--text3)",fontSize:11}}>{r.phone}</div>
                        </td>
                        <td style={{textTransform:"capitalize",color:"var(--text2)"}}>{r.vehicle_type}</td>
                        <td><BranchBadge branchName={r.branch_name||(r.branch_id?`Branch #${r.branch_id}`:null)} isMyBranch={!!r.is_my_branch} compact/></td>
                        <td style={{color:"var(--accent)"}}>★ {parseFloat(r.rating||0).toFixed(1)}</td>
                        <td>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            {statusDot("available")}
                            <span style={{color:"var(--green)",fontSize:12}}>Online</span>
                          </div>
                        </td>
                        <td>
                          {r.current_lat ? (
                            <button className="btn btn-ghost btn-sm" onClick={() => setMapRider(r)}>
                              📍 View
                            </button>
                          ) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Leaderboard */}
        <div className="card">
          <div className="card-header"><span className="card-title">🏆 Leaderboard</span></div>

          {/* Mobile leaderboard cards */}
          <div className="riders-mobile-cards">
            {leaderboard.length === 0
              ? <div className="empty-state"><p>No data</p></div>
              : leaderboard.slice(0, 10).map((r, i) => (
              <div key={i} className="rider-card" style={{background:(isBranchMgr && !r.is_my_branch)?"rgba(90,90,114,0.04)":undefined}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontWeight:700,fontSize:20,width:28,textAlign:"center",flexShrink:0,color:i===0?"var(--accent)":i===1?"#aaa":i===2?"#cd7f32":"var(--text3)"}}>
                    {i===0?"🥇":i===1?"🥈":i===2?"🥉":`#${i+1}`}
                  </span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:14}}>{r.name}</div>
                    <div style={{fontSize:11,color:"var(--text3)"}}>
                      {r.total_deliveries} deliveries
                      {r.branch_name && <> · <BranchBadge branchName={r.branch_name} isMyBranch={!!r.is_my_branch} compact/></>}
                    </div>
                  </div>
                  <span style={{color:"var(--accent)",fontWeight:700}}>★ {parseFloat(r.rating||0).toFixed(1)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop leaderboard table */}
          <div className="riders-desktop-table table-wrap">
            <table>
              <thead><tr><th>#</th><th>Rider</th><th>Branch</th><th>Deliveries</th><th>Rating</th></tr></thead>
              <tbody>
                {leaderboard.length === 0
                  ? <tr><td colSpan={5}><div className="empty-state"><p>No data</p></div></td></tr>
                  : leaderboard.slice(0, 10).map((r, i) => (
                  <tr key={i} style={{background:(isBranchMgr && !r.is_my_branch)?"rgba(90,90,114,0.04)":undefined}}>
                    <td style={{color:i===0?"var(--accent)":i===1?"#aaa":i===2?"#cd7f32":"var(--text3)",fontWeight:700}}>
                      {i===0?"🥇":i===1?"🥈":i===2?"🥉":`#${i+1}`}
                    </td>
                    <td className="fw-bold">{r.name}</td>
                    <td><BranchBadge branchName={r.branch_name||(r.branch_id?`Branch #${r.branch_id}`:null)} isMyBranch={!!r.is_my_branch} compact/></td>
                    <td className="mono">{r.total_deliveries}</td>
                    <td style={{color:"var(--accent)"}}>★ {parseFloat(r.rating||0).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Map modal */}
      {mapRider && (
        <div onClick={() => setMapRider(null)} style={{
          position:"fixed", inset:0, background:"rgba(0,0,0,0.7)",
          zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center",
          padding:"16px",
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background:"var(--bg2)", border:"1px solid var(--border2)",
            borderRadius:16, overflow:"hidden", width:560, maxWidth:"100%",
          }}>
            <div style={{ padding:"16px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
              <div style={{minWidth:0}}>
                <div style={{ fontWeight:700 }}>{mapRider.name}</div>
                <div style={{ fontSize:12, color:"var(--text3)", marginTop:2 }}>
                  {mapRider.vehicle_type} · ★ {parseFloat(mapRider.rating||0).toFixed(1)} · {mapRider.branch_name || "—"}
                </div>
                <div style={{ fontSize:11, color:"var(--text3)", marginTop:2, fontFamily:"var(--font-mono)" }}>
                  {parseFloat(mapRider.current_lat).toFixed(5)}, {parseFloat(mapRider.current_lng).toFixed(5)}
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" style={{flexShrink:0}} onClick={() => setMapRider(null)}>✕ Close</button>
            </div>
            <iframe
              title="rider-map"
              width="100%"
              height="300"
              frameBorder="0"
              style={{ display:"block" }}
              src={`https://maps.google.com/maps?q=${mapRider.current_lat},${mapRider.current_lng}&z=15&output=embed`}
            />
          </div>
        </div>
      )}
    </div>
  );
}